import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../authProvider.mjs';
import userManagementService from '../service/userManagementService.mjs';
import { FaUsers, FaUserShield, FaSearch, FaUserSlash, FaUserCheck, FaEdit } from 'react-icons/fa';
import { secureLogger } from '../../utils/secureLogger.mjs';
import PageShell from '../../components/PageShell.mjs';

const ROLE_LABELS = {
  user: 'User',
  admin: 'Admin',
  auditor: 'Auditor',
  regional_director: 'Regional Director',
  global_director: 'Global Director',
  finance_director: 'Finance Director'
};

const ROLE_COLORS = {
  user: 'bg-gray-100 text-gray-700',
  admin: 'bg-blue-100 text-blue-700',
  auditor: 'bg-purple-100 text-purple-700',
  regional_director: 'bg-orange-100 text-orange-700',
  global_director: 'bg-green-100 text-green-700',
  finance_director: 'bg-red-100 text-red-700'
};

/** Engagement types an admin can set (backend config/engagement.mjs). */
const ENGAGEMENT_OPTIONS = [
  { value: 'staff', label: 'Staff' },
  { value: 'independent_contractor', label: 'Independent contractor' },
  { value: 'coach', label: 'Coach' },
];

/** Engagements whose claims end with the auditor - keep in step with the server's list. */
const AUDITOR_FINAL_ENGAGEMENTS = ['independent_contractor', 'coach'];

const UserManagementPage = () => {
  const { user: currentUser, isFinanceDirector: _isFinanceDirector, isGlobalDirector: _isGlobalDirector } = useContext(AuthContext);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [showSuspended, setShowSuspended] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Available options
  const [regions, setRegions] = useState([]);
  const [assignableRoles, setAssignableRoles] = useState([]);
  const [canManageAllRegions, setCanManageAllRegions] = useState(false);

  // Modals
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [newRegion, setNewRegion] = useState('');
  const [newEngagement, setNewEngagement] = useState('staff');
  const [suspendReason, setSuspendReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Stats
  const [stats, setStats] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await userManagementService.getUsers({
        page,
        limit: 20,
        search: search || undefined,
        role: roleFilter || undefined,
        region: regionFilter || undefined,
        includeDeleted: showSuspended ? 'true' : 'false'
      });
      setUsers(result.data.users);
      setTotalPages(result.data.pagination.pages);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter, regionFilter, showSuspended]);

  const fetchInitialData = useCallback(async () => {
    try {
      // Fetch assignable roles
      const rolesResult = await userManagementService.getAssignableRoles();
      setAssignableRoles(rolesResult.data.assignableRoles);
      setCanManageAllRegions(rolesResult.data.canManageAllRegions);

      // Fetch regions if user can manage all
      if (rolesResult.data.canManageAllRegions) {
        const regionsResult = await userManagementService.getRegions();
        setRegions(regionsResult.data.regions);
      }

      // Fetch stats
      const statsResult = await userManagementService.getStats();
      setStats(statsResult.data);
    } catch (err) {
      secureLogger.error('Error fetching initial data', { status: err.response?.status });
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const openRoleModal = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setNewRegion(user.region || '');
    setNewEngagement(user.engagementType || 'staff');
    setShowRoleModal(true);
  };

  const openSuspendModal = (user) => {
    setSelectedUser(user);
    setSuspendReason('');
    setShowSuspendModal(true);
  };

  // Which row is mid-save, so its select can be disabled without freezing the
  // whole table.
  const [engagementBusyId, setEngagementBusyId] = useState(null);

  /**
   * Move someone between staff and contractor.
   *
   * Confirmed first: it changes who approves their money, and the two options
   * sit one click apart in a dropdown.
   */
  /**
   * Ask before moving someone between staff and contractor. Shared by the
   * table's select and the Edit dialog, so the warning is the same wherever
   * it is changed. Only the contractor route currently ends at the auditor; a
   * coach follows the ordinary chain until that is decided, and the prompt
   * says so rather than implying the label changes something it does not.
   */
  const confirmEngagement = (user, engagementType) => {
    const LABELS = {
      staff: 'staff',
      independent_contractor: 'an independent contractor',
      coach: 'a coach',
    };
    // Coaches end at the auditor too, as contractors do (2026-10-21) - the
    // server's AUDITOR_FINAL_ENGAGEMENTS (backend src/config/engagement.mjs).
    const endsWithAuditor = AUDITOR_FINAL_ENGAGEMENTS.includes(engagementType);
    return window.confirm(
      `Mark ${user.firstName} ${user.lastName} ${LABELS[engagementType] || engagementType}?\n\n`
      + (endsWithAuditor
        ? 'Their future claims will be finally approved by the auditor and will '
          + 'not go to the regional or global directors, or into a monthly report.'
        : 'Their future claims will follow the full approval chain through the '
          + 'monthly report.')
    );
  };

  const handleEngagementChange = async (user, engagementType) => {
    if ((user.engagementType || 'staff') === engagementType) return;
    if (!confirmEngagement(user, engagementType)) return;

    setEngagementBusyId(user.id);
    setError(null);
    try {
      const res = await userManagementService.changeUserEngagement(user.id, engagementType);
      // The server's sentence says what changes for their claims; it used to
      // be thrown away, so the only sign the change took was the select itself.
      setSuccess(res?.message || `${user.firstName}'s engagement updated`);
      fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setEngagementBusyId(null);
    }
  };

  /**
   * Save the Edit dialog: role (and region), then engagement.
   *
   * The dialog used to be role-only - engagement could be set solely from the
   * table's inline select - so "Edit" offered no way to mark an existing user a
   * contractor or a coach. The role goes first because the server only accepts
   * an engagement for someone who is a claimant at that moment.
   */
  const handleChangeRole = async () => {
    if (!selectedUser || !newRole) return;

    const roleChanged = newRole !== selectedUser.role
      || (newRole === 'regional_director' && newRegion && newRegion !== (selectedUser.region || ''));
    const currentEngagement = selectedUser.engagementType || 'staff';
    const engagementChanged = newRole === 'user' && newEngagement !== currentEngagement;

    if (!roleChanged && !engagementChanged) {
      setShowRoleModal(false);
      return;
    }
    if (engagementChanged && !confirmEngagement(selectedUser, newEngagement)) return;

    setActionLoading(true);
    setError(null);
    const done = [];
    try {
      if (roleChanged) {
        await userManagementService.changeUserRole(
          selectedUser.id,
          newRole,
          newRole === 'regional_director' ? newRegion : null
        );
        done.push(`role changed to ${ROLE_LABELS[newRole] || newRole}`);
      }
      if (engagementChanged) {
        await userManagementService.changeUserEngagement(selectedUser.id, newEngagement);
        done.push(`engagement set to ${ENGAGEMENT_OPTIONS.find(o => o.value === newEngagement)?.label || newEngagement}`);
      }
      setSuccess(`${selectedUser.firstName}: ${done.join(' and ')}`);
      setShowRoleModal(false);
      fetchUsers();
    } catch (err) {
      // A role change that went through before the engagement failed is
      // still shown, so the table is not left describing the old role.
      setError(done.length ? `${selectedUser.firstName}: ${done.join(' and ')}, but ${err.message}` : err.message);
      if (done.length) fetchUsers();
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    setError(null);
    try {
      await userManagementService.suspendUser(selectedUser.id, suspendReason);
      setSuccess(`${selectedUser.firstName} ${selectedUser.lastName} has been suspended`);
      setShowSuspendModal(false);
      fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async (user) => {
    setActionLoading(true);
    setError(null);
    try {
      await userManagementService.reactivateUser(user.id);
      setSuccess(`${user.firstName} ${user.lastName} has been reactivated`);
      fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Clear success message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  return (
    <>
    <PageShell title="User Management" subtitle="Manage user roles and permissions" icon={FaUsers}>
        {/* Messages */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">×</button>
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 border-t-2 border-t-stanford-red">
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 leading-none">{stats.totalUsers}</div>
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500 mt-1.5">Total Users</div>
            </div>
            {Object.entries(stats.roleCounts || {}).map(([role, count]) => (
              <div key={role} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 leading-none">{count}</div>
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500 mt-1.5">{ROLE_LABELS[role] || role}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
          <form onSubmit={handleSearch} className="grid grid-cols-2 md:flex md:flex-wrap gap-3 md:gap-4 items-end">
            <div className="col-span-2 md:col-span-1 md:flex-1 md:min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10 pointer-events-none" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full min-h-[44px] md:min-h-0 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent bg-white"
                />
              </div>
            </div>

            <div className="md:w-40">
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                value={roleFilter}
                onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
                className="w-full min-h-[44px] md:min-h-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red"
              >
                <option value="">All Roles</option>
                {Object.entries(ROLE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            {canManageAllRegions && (
              <div className="md:w-40">
                <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
                <select
                  value={regionFilter}
                  onChange={(e) => { setRegionFilter(e.target.value); setPage(1); }}
                  className="w-full min-h-[44px] md:min-h-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red"
                >
                  <option value="">All Regions</option>
                  {regions.map(region => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex items-center gap-2 min-h-[44px] md:min-h-0">
              <input
                type="checkbox"
                id="showSuspended"
                checked={showSuspended}
                onChange={(e) => { setShowSuspended(e.target.checked); setPage(1); }}
                className="rounded"
              />
              <label htmlFor="showSuspended" className="text-sm text-gray-700">Show Suspended</label>
            </div>

            <button
              type="submit"
              className="min-h-[44px] md:min-h-0 px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors"
            >
              Search
            </button>
          </form>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-10 h-10 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Loading users...</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full responsive-table">
                  <thead className="bg-stanford-red text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Role</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Engagement</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Region</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className={`border-b border-gray-200 hover:bg-gray-50 ${user.isDeleted ? 'bg-red-50' : ''}`}>
                        <td className="px-4 py-3" data-cell="title">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="shrink-0 w-10 h-10 rounded-full bg-stanford-red text-white flex items-center justify-center font-bold">
                              {user.firstName?.[0]}{user.lastName?.[0]}
                            </div>
                            <div>
                              <div className="font-medium text-gray-800">{user.firstName} {user.lastName}</div>
                              <div className="text-sm text-gray-500">{user.position || 'No position'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 break-all" data-label="Email">{user.email}</td>
                        <td className="px-4 py-3" data-label="Role">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${ROLE_COLORS[user.role]}`}>
                            {ROLE_LABELS[user.role] || user.role}
                          </span>
                        </td>
                        {/* Staff or contractor. Only a claimant can be a
                            contractor - marking an approver one would say their
                            own claims skip the directors while they sit on that
                            very chain - so everyone else shows a dash. */}
                        <td className="px-4 py-3" data-label="Engagement">
                          {user.role !== 'user' ? (
                            <span className="text-sm text-gray-400">-</span>
                          ) : (
                            <select
                              value={user.engagementType || 'staff'}
                              disabled={engagementBusyId === user.id}
                              onChange={(e) => handleEngagementChange(user, e.target.value)}
                              aria-label={`Engagement for ${user.firstName} ${user.lastName}`}
                              className="min-h-[40px] md:min-h-0 px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-50"
                            >
                              <option value="staff">Staff</option>
                              <option value="independent_contractor">Independent contractor</option>
                              <option value="coach">Coach</option>
                            </select>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700" data-label="Region">{user.region || '-'}</td>
                        <td className="px-4 py-3" data-label="Status">
                          {user.isDeleted ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              Suspended
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              Active
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3" data-cell="action">
                          <div className="flex gap-2">
                            {/* Don't show actions for current user */}
                            {user.id !== currentUser?.id && (
                              <>
                                <button
                                  onClick={() => openRoleModal(user)}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors gap-2 border border-blue-200 md:border-0"
                                  title="Edit user"
                                  aria-label="Edit user"
                                >
                                  <FaEdit aria-hidden="true" />
                                  <span className="md:hidden text-sm font-medium">Edit</span>
                                </button>
                                {user.isDeleted ? (
                                  <button
                                    onClick={() => handleReactivate(user)}
                                    disabled={actionLoading}
                                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                                    title="Reactivate User"
                                    aria-label="Reactivate user"
                                  >
                                    <FaUserCheck aria-hidden="true" />
                                    <span className="md:hidden text-sm font-medium ml-2">Reactivate</span>
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => openSuspendModal(user)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors gap-2 border border-red-200 md:border-0"
                                    title="Suspend User"
                                    aria-label="Suspend user"
                                  >
                                    <FaUserSlash aria-hidden="true" />
                                    <span className="md:hidden text-sm font-medium">Suspend</span>
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {users.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  No users found
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 p-4 border-t">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1">Page {page} of {totalPages}</span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
    </PageShell>

      {/* Change Role Modal */}
      {showRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FaUserShield className="text-stanford-red" />
              Edit User
            </h2>
            <p className="text-gray-600 mb-4">
              <strong>{selectedUser.firstName} {selectedUser.lastName}</strong>
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">New Role</label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="w-full min-h-[44px] md:min-h-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red"
              >
                {assignableRoles.map(role => (
                  <option key={role} value={role}>{ROLE_LABELS[role] || role}</option>
                ))}
              </select>
            </div>

            {/* Staff, contractor or coach. Only a claimant files claims, so
                only a claimant has one - the server refuses it for anyone else,
                and moving someone to an approver role resets them to staff. */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Engagement</label>
              {newRole === 'user' ? (
                <>
                  <select
                    value={newEngagement}
                    onChange={(e) => setNewEngagement(e.target.value)}
                    className="w-full min-h-[44px] md:min-h-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red"
                  >
                    {ENGAGEMENT_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {AUDITOR_FINAL_ENGAGEMENTS.includes(newEngagement)
                      ? "The auditor gives final approval on their claims; they don't go to the directors or into a monthly report."
                      : 'Their claims follow the full approval chain through the monthly report.'}
                  </p>
                </>
              ) : (
                <p className="text-sm text-gray-500">
                  Only claimants have an engagement type.
                  {(selectedUser.engagementType || 'staff') !== 'staff' && selectedUser.role === 'user'
                    && ' Changing this person to an approver role resets them to staff.'}
                </p>
              )}
            </div>

            {newRole === 'regional_director' && canManageAllRegions && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
                <select
                  value={newRegion}
                  onChange={(e) => setNewRegion(e.target.value)}
                  className="w-full min-h-[44px] md:min-h-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red"
                >
                  <option value="">Select Region</option>
                  {regions.map(region => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowRoleModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleChangeRole}
                disabled={actionLoading || !newRole}
                className="px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red disabled:opacity-50"
              >
                {actionLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend User Modal */}
      {showSuspendModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FaUserSlash className="text-red-600" />
              Suspend User
            </h2>
            <p className="text-gray-600 mb-4">
              Are you sure you want to suspend <strong>{selectedUser.firstName} {selectedUser.lastName}</strong>?
              They will no longer be able to access the system.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
              <textarea
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="Enter reason for suspension..."
                rows={3}
                className="w-full min-h-[44px] md:min-h-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowSuspendModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleSuspend}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading ? 'Suspending...' : 'Suspend User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserManagementPage;
